import React, { useState } from 'react';
import ChevronDown from '../Icons/ChevronDown';
import { isNullOrUndefined } from '../../helpers/object-helpers';

import styles from './Toggle.module.scss';

export interface IToggleOption {
  text: string;
  value: string;
}

interface IToggleOptionInternal extends IToggleOption {
  groupName: string;
  optionSelectedHandler: (id: string) => void;
  closeOptionsHandler: () => void;
  isChecked: boolean;
}

export interface IToggle {
  id: string;
  options: IToggleOption[];
  defaultOption?: IToggleOption;
  name: string;
  legend: string;
  onChange?: (value: IToggleOption) => void;
}

export default function Toggle({ id, options, name, legend, defaultOption, onChange }: IToggle) {
  const [selectedOption, setSelectedOption] = useState<IToggleOption>(
    options.find((option) => option.value === defaultOption?.value) ?? options[0],
  );

  const [showOptionsInMobile, setShowOptionsInMobile] = useState(true);

  const handleOptionSelection = (value: string) => {
    const selected = options.find((option) => option.value === value);

    if (selected == null) return;

    setSelectedOption(selected);

    if (!isNullOrUndefined(onChange)) onChange(selected);
  };

  const handleCloseOptions = () => {
    setShowOptionsInMobile(false);
  };

  return (
    <fieldset id={id} className={styles.toggle} role="radiogroup">
      <legend>{legend}</legend>
      <button
        aria-hidden="true"
        type="button"
        onClick={() => {
          setShowOptionsInMobile(!showOptionsInMobile);
        }}
      >
        {selectedOption.text}
        <ChevronDown />
      </button>
      <div className={[styles.options, !showOptionsInMobile ? styles.hideInMobile : ''].join(' ')}>
        {options.map((option) => (
          <ToggleOption
            key={option.value}
            groupName={name}
            {...option}
            optionSelectedHandler={handleOptionSelection}
            closeOptionsHandler={handleCloseOptions}
            isChecked={selectedOption.value === option.value}
          />
        ))}
      </div>
    </fieldset>
  );
}

function ToggleOption({
  text,
  value,
  groupName,
  optionSelectedHandler,
  closeOptionsHandler,
  isChecked,
}: IToggleOptionInternal) {
  const id = `toggle-option-${value.replace(/ /g, '-')}`;

  const handleInputChange = () => {
    optionSelectedHandler(value);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.code === 'Enter' || event.code === 'Space') {
      closeOptionsHandler();
    }
  };

  const handleLabelClick = () => {
    optionSelectedHandler(value);
    closeOptionsHandler();
  };

  return (
    <>
      <input
        type="radio"
        id={id}
        name={groupName}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        checked={isChecked}
      />
      <label htmlFor={id} onClick={handleLabelClick}>
        {text}
      </label>
    </>
  );
}
